import { useEffect, useState } from "react";
import {
  BarChart3, Users, MessageSquareText, ShieldCheck, TrendingUp, AlertTriangle,
  BookMarked, Layers, Loader2, HelpCircle, Lock, Download, Trash2, Sparkles,
  Clock, Link2, EyeOff, Plus, ChevronRight, LogIn, KeyRound, ThumbsUp, ThumbsDown, UserCheck,
  Briefcase, ScrollText, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/context/UserContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  groen: { label: "Groen", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
  geel: { label: "Geel", color: "bg-amber-400", text: "text-amber-700", bg: "bg-amber-50" },
  rood: { label: "Rood", color: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50" },
  verduidelijking: { label: "Verduidelijking", color: "bg-indigo-500", text: "text-indigo-700", bg: "bg-indigo-50" },
};

const Card = ({ title, icon: Icon, accent = "text-slate-500", children, testid }) => (
  <section data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4.5 h-4.5 ${accent}`} />
      <h2 className="text-sm font-heading font-bold text-slate-800">{title}</h2>
    </div>
    {children}
  </section>
);

const fmtTijd = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <p className="mt-2 text-2xl font-heading font-bold text-slate-900">{value}</p>
  </div>
);

const BarList = ({ items, labelKey = "label", max, empty = "Nog geen data", testid }) => {
  const top = max || Math.max(1, ...items.map((i) => i.aantal));
  if (!items.length) return <p className="text-xs text-slate-400 italic">{empty}</p>;
  return (
    <ul className="space-y-2.5" data-testid={testid}>
      {items.map((it, idx) => (
        <li key={idx} className="text-xs">
          <div className="flex justify-between gap-3 mb-1 min-w-0">
            <span className="text-slate-700 truncate min-w-0">{it[labelKey]}</span>
            <span className="font-bold text-slate-900 tabular-nums shrink-0">{it.aantal}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-blue-500" style={{ width: `${(it.aantal / top) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const QuestionList = ({ items, empty, tone = "text-slate-700", testid }) => {
  if (!items.length) return <p className="text-xs text-slate-400 italic">{empty}</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid={testid}>
      {items.map((q, idx) => (
        <li key={idx} className="py-2 flex items-start justify-between gap-3">
          <span className={`text-xs ${tone} min-w-0 break-words`}>{q.vraag}</span>
          <span className="text-xs font-bold text-slate-900 tabular-nums shrink-0 bg-slate-100 rounded px-1.5 py-0.5">{q.aantal}×</span>
        </li>
      ))}
    </ul>
  );
};

const FG_OPTIES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "menu_acceptabel"];

const FunctiecheckFeedback = () => {
  const [stats, setStats] = useState(null);
  const [sel, setSel] = useState(null);
  const [fg, setFg] = useState("");
  const [refFunctie, setRefFunctie] = useState("");
  const [gNaam, setGNaam] = useState("");
  const [gBeschrijving, setGBeschrijving] = useState("");
  const [gAfdeling, setGAfdeling] = useState("");
  const [saving, setSaving] = useState(false);

  const laad = () => api.get("/admin/functie-feedback/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { laad(); }, []);

  const openCase = (item) => {
    setSel(item);
    setFg("");
    setRefFunctie("");
    setGNaam(item.invoer?.functienaam || "");
    setGBeschrijving(item.invoer?.beschrijving || "");
    setGAfdeling(item.invoer?.afdeling || "");
  };

  const maakCase = async () => {
    setSaving(true);
    try {
      await api.post("/admin/benchmark-case", {
        feedback_id: sel.id,
        verwachte_functiegroep: fg,
        verwachte_referentiefunctie: refFunctie.trim() || null,
        geschoonde_invoer: { functienaam: gNaam, beschrijving: gBeschrijving, afdeling: gAfdeling },
      });
      toast.success("Benchmarkcase aangemaakt en gesynchroniseerd naar de dynamische testpoort.");
      setSel(null);
      laad();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Benchmarkcase aanmaken mislukt.");
    } finally { setSaving(false); }
  };

  const exporteer = async () => {
    try {
      const r = await api.get("/admin/functie-feedback/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `functiecheck-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Feedback geëxporteerd als CSV (zonder gebruikersgegevens).");
    } catch { toast.error("Export mislukt."); }
  };

  if (!stats) return null;
  const t = stats.tellers;
  const roodActief = stats.rood_incidenten > 0;

  return (
    <Card title="Functiecheck-feedback (pilot)" icon={Briefcase} accent="text-amber-600" testid="feedback-dashboard">
      <p className="text-xs text-slate-500 mb-4">Eén-klik-feedback van pilotgebruikers op de Functiecheck-uitkomst. Anoniem; ❓ telt niet mee in het kwaliteitspercentage.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
        <div className="rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-200" data-testid="feedback-kpi-klopt">
          <p className="text-[11px] text-emerald-700 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Klopt</p>
          <p className="text-lg font-heading font-bold text-slate-900">{t.klopt}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-rose-50 border border-rose-200" data-testid="feedback-kpi-klopt-niet">
          <p className="text-[11px] text-rose-700 flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Klopt niet</p>
          <p className="text-lg font-heading font-bold text-slate-900">{t.klopt_niet}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-slate-50 border border-slate-200" data-testid="feedback-kpi-weet-niet">
          <p className="text-[11px] text-slate-500 flex items-center gap-1"><HelpCircle className="w-3 h-3" /> Weet ik niet</p>
          <p className="text-lg font-heading font-bold text-slate-900">{t.weet_niet}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-blue-50 border border-blue-200" data-testid="feedback-kpi-kwaliteit">
          <p className="text-[11px] text-blue-700">Kwaliteit</p>
          <p className="text-lg font-heading font-bold text-slate-900">
            {stats.kwaliteit_pct != null ? `${stats.kwaliteit_pct}%` : "—"}
            <span className="text-xs font-normal text-slate-400"> (n={stats.beoordeelbaar})</span>
          </p>
        </div>
        <div data-testid="feedback-kpi-rood-incident"
          className={`rounded-lg px-3 py-2 border ${roodActief ? "bg-rose-600 border-rose-700 text-white" : "bg-slate-50 border-slate-200"}`}>
          <p className={`text-[11px] flex items-center gap-1 ${roodActief ? "text-white font-semibold" : "text-slate-500"}`}>
            {roodActief && <AlertTriangle className="w-3 h-3" />} 👎 bij vertrouwen Hoog
          </p>
          <p className={`text-lg font-heading font-bold ${roodActief ? "text-white" : "text-slate-900"}`}>{stats.rood_incidenten}</p>
          {roodActief && <p className="text-[10px] font-semibold">handmatige review vereist</p>}
        </div>
        <div className="rounded-lg px-3 py-2 bg-amber-50 border border-amber-200" data-testid="feedback-kpi-na-menu">
          <p className="text-[11px] text-amber-700">👎 na keuzemenu</p>
          <p className="text-lg font-heading font-bold text-slate-900">{stats.klopt_niet_na_keuzemenu}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <p className="text-xs font-semibold text-rose-600 mb-2">Recente 👎-meldingen (max 20)</p>
          {stats.top_klopt_niet.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nog geen 👎-feedback ontvangen.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="feedback-klopt-niet-lijst">
              {stats.top_klopt_niet.map((item) => (
                <li key={item.id} className="py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0 text-xs">
                    <p className="font-medium text-slate-800 truncate">{item.invoer?.functienaam || "(geen functienaam)"}
                      <span className="text-slate-400 font-normal"> → fg {item.uitkomst?.functiegroep || "?"} · {item.uitkomst?.vertrouwensniveau || "—"}{item.uitkomst?.via_keuzemenu ? " · via menu" : ""}</span>
                    </p>
                    <p className="text-slate-500 truncate">{item.invoer?.beschrijving || "—"}</p>
                  </div>
                  {item.geconverteerd ? (
                    <Badge variant="secondary" className="text-[10px] shrink-0">Benchmarkcase ✓</Badge>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0"
                      data-testid={`feedback-maak-case-${item.id}`} onClick={() => openCase(item)}>
                      Maak benchmarkcase
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-2">Signalering per referentiefunctie</p>
          {stats.per_referentiefunctie.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nog geen beoordeelbare feedback.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="feedback-signalen">
              {stats.per_referentiefunctie.map((s) => (
                <li key={s.referentiefunctie} className={`flex justify-between gap-2 text-xs rounded px-2 py-1.5 ${s.signaal ? "bg-rose-50 border border-rose-200" : "bg-slate-50"}`}>
                  <span className={`truncate min-w-0 ${s.signaal ? "text-rose-700 font-medium" : "text-slate-700"}`}>
                    {s.signaal && <AlertTriangle className="w-3 h-3 inline mr-1" />}{s.referentiefunctie}
                  </span>
                  <span className="shrink-0 text-slate-500">{s.klopt_niet}/{s.n} 👎 ({s.pct_klopt_niet}%)</span>
                </li>
              ))}
            </ul>
          )}
          {stats.top_functienamen.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-600 mb-2">Kwaliteit per ingevoerde functienaam</p>
              <ul className="space-y-1" data-testid="feedback-per-functienaam">
                {stats.top_functienamen.map((f) => (
                  <li key={f.functienaam} className="flex justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span className="text-slate-700 truncate min-w-0">{f.functienaam}</span>
                    <span className="font-bold text-slate-900 shrink-0">{f.kwaliteit_pct}% <span className="font-normal text-slate-400">(n={f.n})</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <Button variant="outline" onClick={exporteer} data-testid="feedback-export-btn" className="gap-2">
          <Download className="w-4 h-4" /> Feedback exporteren (CSV)
        </Button>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="benchmark-case-dialog" className="max-w-lg">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle>Benchmarkcase aanmaken</DialogTitle>
                <DialogDescription>
                  Controleer en anonimiseer de invoer (geen namen of herleidbare gegevens). De case wordt
                  toegevoegd aan de dynamische regressiepoort.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Verwachte functiegroep *</label>
                  <Select value={fg} onValueChange={setFg}>
                    <SelectTrigger data-testid="benchmark-fg-select" className="h-9">
                      <SelectValue placeholder="Kies functiegroep" />
                    </SelectTrigger>
                    <SelectContent>
                      {FG_OPTIES.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`benchmark-fg-${o}`}>
                          {o === "menu_acceptabel" ? "Keuzemenu is acceptabel" : `Functiegroep ${o}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Verwachte referentiefunctie (optioneel)</label>
                  <Input value={refFunctie} onChange={(e) => setRefFunctie(e.target.value)} data-testid="benchmark-ref-input" placeholder="bijv. Ovenman" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Geschoonde functienaam</label>
                  <Input value={gNaam} onChange={(e) => setGNaam(e.target.value)} data-testid="benchmark-naam-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Geschoonde beschrijving *</label>
                  <Textarea value={gBeschrijving} onChange={(e) => setGBeschrijving(e.target.value)} data-testid="benchmark-beschrijving-input" className="min-h-[80px]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Geschoonde afdeling</label>
                  <Input value={gAfdeling} onChange={(e) => setGAfdeling(e.target.value)} data-testid="benchmark-afdeling-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSel(null)} data-testid="benchmark-annuleer-btn">Annuleren</Button>
                <Button onClick={maakCase} disabled={saving || !fg || !(gNaam.trim() || gBeschrijving.trim())}
                  data-testid="benchmark-opslaan-btn" className="bg-slate-900">
                  {saving ? "Bezig…" : "Benchmarkcase opslaan"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const AkkoordenCard = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/admin/akkoorden").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  if (!info) return null;
  const vDatum = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
  return (
    <Card title="Akkoorden — voorwaarden & privacy" icon={ScrollText} accent="text-teal-600" testid="card-akkoorden">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        <Kpi label="Actieve gebruikers" value={info.actieve_gebruikers} icon={Users} accent="text-slate-600" testid="akkoorden-actief" />
        <Kpi label="Actueel akkoord" value={info.met_actueel_akkoord} icon={CheckCircle2} accent="text-emerald-600" testid="akkoorden-actueel" />
        <Kpi label="Verouderd / ontbreekt" value={info.met_verouderd_akkoord} icon={AlertTriangle} accent={info.met_verouderd_akkoord > 0 ? "text-amber-600" : "text-slate-400"} testid="akkoorden-verouderd" />
        <Kpi label="Documentversie" value={info.huidige_voorwaarden_versie} icon={ScrollText} accent="text-teal-600" testid="akkoorden-versie" />
        <Kpi label="Laatst gewijzigd" value={vDatum(info.laatst_gewijzigd)} icon={Clock} accent="text-slate-500" testid="akkoorden-gewijzigd" />
      </div>
      {info.met_verouderd_akkoord > 0 && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="akkoorden-waarschuwing">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{info.met_verouderd_akkoord} gebruiker{info.met_verouderd_akkoord === 1 ? "" : "s"} moet{info.met_verouderd_akkoord === 1 ? "" : "en"} bij de volgende login opnieuw akkoord geven met de actuele documenten.</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="akkoorden-tabel">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 font-medium">Gebruiker</th>
              <th className="py-2 pr-3 font-medium">Voorwaarden</th>
              <th className="py-2 pr-3 font-medium">Privacy</th>
              <th className="py-2 pr-3 font-medium">AI-akkoord</th>
              <th className="py-2 pr-3 font-medium">Datum</th>
            </tr>
          </thead>
          <tbody>
            {info.gebruikers.map((g) => (
              <tr key={g.email} className="border-b border-slate-100 last:border-0" data-testid={`akkoord-rij-${g.email}`}>
                <td className="py-2 pr-3">
                  <span className="text-slate-800 font-medium">{g.naam || g.email}</span>
                  {!g.actueel && (
                    <span className="block text-[11px] text-amber-700 mt-0.5" data-testid={`akkoord-melding-${g.email}`}>
                      ⚠ Moet bij volgende login opnieuw akkoord geven met de actuele documenten.
                    </span>
                  )}
                </td>
                <td className={`py-2 pr-3 ${g.voorwaarden_versie === info.huidige_voorwaarden_versie ? "text-emerald-700" : "text-amber-700"}`}>{g.voorwaarden_versie || "—"}</td>
                <td className={`py-2 pr-3 ${g.privacy_versie === info.huidige_privacy_versie ? "text-emerald-700" : "text-amber-700"}`}>{g.privacy_versie || "—"}</td>
                <td className="py-2 pr-3">{g.ai_besef_akkoord ? <span className="text-emerald-700">Ja</span> : <span className="text-amber-700">Nee</span>}</td>
                <td className="py-2 pr-3 text-slate-500">{vDatum(g.akkoord_datum)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Read-only overzicht op basis van de akkoordregistratie (juridisch_akkoord_log). Akkoorden kunnen niet handmatig worden aangepast.</p>
    </Card>
  );
};

export default function AuditDashboard() {
  const { user } = useUser();
  const isAdmin = user && ["beheerder", "superbeheerder"].includes(user.rol);
  const isSuper = user && user.rol === "superbeheerder";
  const [data, setData] = useState(null);
  const [dagen, setDagen] = useState("0");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missing, setMissing] = useState([]);
  const [onbekend, setOnbekend] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mapsTo, setMapsTo] = useState("");
  const [targetId, setTargetId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const laadDashboard = () => {
    setLoading(true);
    api.get("/admin/dashboard", { params: { dagen: Number(dagen) } })
      .then((r) => { setData(r.data); setError(null); })
      .catch(() => setError("Kon het dashboard niet laden."))
      .finally(() => setLoading(false));
  };

  const laadMissing = () => {
    if (!isSuper) return;
    api.get("/admin/missing-terms").then((r) => setMissing(r.data.termen)).catch(() => {});
  };

  const laadOnbekend = () => {
    api.get("/admin/onbekende-termen").then((r) => setOnbekend(r.data.termen)).catch(() => {});
  };

  useEffect(() => { if (isAdmin) laadDashboard(); /* eslint-disable-next-line */ }, [dagen, isAdmin]);
  useEffect(() => { laadMissing(); /* eslint-disable-next-line */ }, [isSuper]);
  useEffect(() => { if (isAdmin) laadOnbekend(); /* eslint-disable-next-line */ }, [isAdmin]);

  const openTerm = (t) => { setSelected(t); setMapsTo(t.voorgestelde_normalisatie || t.term); setTargetId(null); };

  const voerActieUit = async (action) => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = { action, term: selected.term };
      if (action === "add_term") payload.maps_to = mapsTo.trim();
      if (action === "add_synonym") {
        payload.target_begrip_id = targetId;
        payload.maps_to = mapsTo.trim();
      }
      await api.post("/admin/begrippen/override", payload);
      const labels = { add_term: "toegevoegd aan register", add_synonym: "toegevoegd als synoniem", ignore: "genegeerd", review: "gemarkeerd voor beoordeling" };
      toast.success(`'${selected.term}' ${labels[action]}.`);
      setSelected(null);
      laadMissing();
      laadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actie mislukt.");
    } finally { setSaving(false); }
  };

  const exporteer = async () => {
    try {
      const r = await api.get("/admin/audit/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `auditlogs-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${r.data.aantal} auditlogs geëxporteerd.`);
    } catch { toast.error("Export mislukt."); }
  };

  const downloadPilotrapport = async () => {
    try {
      const r = await api.get("/admin/pilotrapport", { params: { format: "md" }, responseType: "text" });
      const blob = new Blob([r.data], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pilotrapport-${new Date().toISOString().slice(0, 10)}.md`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Pilotrapport gedownload.");
    } catch { toast.error("Pilotrapport genereren mislukt."); }
  };

  const resetLogs = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/audit/reset");
      toast.success(`Reset voltooid: ${r.data.verwijderde_auditlogs} auditlogs verwijderd.`);
      laadDashboard(); laadMissing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset mislukt.");
    } finally { setResetting(false); }
  };

  if (!isAdmin) {
    return (
      <div data-testid="dashboard-forbidden" className="max-w-md mx-auto mt-16 text-center">
        <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h1 className="font-heading font-bold text-slate-800 text-lg">Geen toegang</h1>
        <p className="text-sm text-slate-500 mt-1">Het audit-dashboard is alleen beschikbaar voor beheerders.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-sm text-rose-600" data-testid="dashboard-error">{error || "Geen data."}</p>;
  }

  const sv = data.kwaliteit.status_verdeling;
  const svTotaal = Object.values(sv).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6" data-testid="audit-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" /> Audit-dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Pilot-inzicht in gebruik, kwaliteit en verbeterkansen.</p>
        </div>
        <Select value={dagen} onValueChange={setDagen}>
          <SelectTrigger data-testid="dashboard-period" className="w-40 h-9 bg-white border-slate-300 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0" data-testid="period-alles">Hele periode</SelectItem>
            <SelectItem value="7" data-testid="period-7">Laatste 7 dagen</SelectItem>
            <SelectItem value="30" data-testid="period-30">Laatste 30 dagen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Privacy note */}
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="dashboard-privacy">
        <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <span>Gegevens zijn geanonimiseerd (geen naam, e-mail of personeelsnummer). Bedoeld voor kwaliteitsverbetering, niet voor personeelsmonitoring.</span>
      </div>

      {/* KPI's */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="kpi-vragen" label="Totaal vragen" value={data.gebruik.totaal_vragen} icon={MessageSquareText} accent="text-blue-600" />
        <Kpi testid="kpi-gebruikers" label="Unieke gebruikers" value={data.gebruik.unieke_gebruikers} icon={Users} accent="text-rose-600" />
        <Kpi testid="kpi-score" label="Gem. retrievalscore" value={data.kwaliteit.gemiddelde_score} icon={TrendingUp} accent="text-emerald-600" />
        <Kpi testid="kpi-clarification" label="Verduidelijkingen" value={data.kwaliteit.clarification_triggers} icon={HelpCircle} accent="text-indigo-600" />
      </div>

      {/* Kwaliteit: status verdeling */}
      <Card title="Kwaliteit — status van antwoorden" icon={ShieldCheck} accent="text-emerald-600" testid="card-kwaliteit">
        <div className="flex h-3 rounded-full overflow-hidden mb-3" data-testid="status-bar">
          {Object.entries(sv).map(([k, v]) => v > 0 && (
            <div key={k} className={STATUS_META[k]?.color || "bg-slate-300"} style={{ width: `${(v / svTotaal) * 100}%` }} title={`${STATUS_META[k]?.label}: ${v}`} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(sv).map(([k, v]) => (
            <div key={k} className={`rounded-lg px-3 py-2 ${STATUS_META[k]?.bg}`} data-testid={`status-${k}`}>
              <p className={`text-[11px] font-medium ${STATUS_META[k]?.text}`}>{STATUS_META[k]?.label}</p>
              <p className="text-lg font-heading font-bold text-slate-900">{v} <span className="text-xs font-normal text-slate-400">({Math.round((v / svTotaal) * 100)}%)</span></p>
            </div>
          ))}
        </div>
        {data.kwaliteit.clarification_redenen.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">Redenen voor verduidelijking</p>
            <BarList items={data.kwaliteit.clarification_redenen} testid="clarification-redenen" />
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Gebruik — meest gestelde vragen" icon={MessageSquareText} accent="text-blue-600" testid="card-gebruik">
          <QuestionList items={data.gebruik.meest_gestelde} empty="Nog geen vragen." testid="meest-gestelde" />
        </Card>
        <Card title="Begrippenregister — meest herkende begrippen" icon={BookMarked} accent="text-violet-600" testid="card-begrippen">
          <BarList items={data.begrippen.top_begrippen} empty="Nog geen begrippen herkend." testid="top-begrippen" />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Brongebruik — per categorie" icon={Layers} accent="text-blue-600" testid="card-brongebruik">
          <BarList items={data.brongebruik.per_categorie} testid="bron-categorie" />
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">Meest geraadpleegde artikelen</p>
            <BarList items={data.brongebruik.top_artikelen} empty="Wordt gevuld zodra nieuwe vragen binnenkomen." testid="top-artikelen" />
          </div>
        </Card>
        <Card title="Brongebruik — per document" icon={Layers} accent="text-slate-500" testid="card-documenten">
          <BarList items={data.brongebruik.per_document} testid="bron-document" />
        </Card>
      </div>

      {/* Verbeterkansen */}
      <Card title="Verbeterkansen" icon={AlertTriangle} accent="text-amber-600" testid="card-verbeterkansen">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div>
            <p className="text-xs font-semibold text-rose-600 mb-2">Top ROOD-vragen (geen bron)</p>
            <QuestionList items={data.verbeterkansen.top_rood} empty="Geen ROOD-vragen 🎉" tone="text-slate-700" testid="top-rood" />
          </div>
          <div>
            <p className="text-xs font-semibold text-amber-600 mb-2">Top GEEL-vragen (zwakke dekking)</p>
            <QuestionList items={data.verbeterkansen.top_geel} empty="Geen GEEL-vragen." tone="text-slate-700" testid="top-geel" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Ontbrekende begrippen</p>
            <BarList items={data.verbeterkansen.ontbrekende_begrippen} empty="Geen kandidaten." testid="ontbrekende-begrippen" />
          </div>
        </div>
      </Card>

      {/* Onbekende functietermen (Dataronde 2) */}
      <Card title="Onbekende functietermen — laatste 30 dagen" icon={HelpCircle} accent="text-orange-600" testid="card-onbekende-termen">
        {onbekend.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="onbekende-termen-leeg">Geen onbekende functienamen ingevoerd — de spreektaal-laag dekt alles wat pilotgebruikers intypen. 🎉</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="py-1.5 font-semibold">Term</th>
                <th className="py-1.5 font-semibold text-right">Aantal</th>
              </tr>
            </thead>
            <tbody>
              {onbekend.map((t) => (
                <tr key={t.term} className="border-b border-slate-50" data-testid={`onbekende-term-${t.term.replace(/\s+/g, "-")}`}>
                  <td className="py-1.5 text-slate-800">{t.term}</td>
                  <td className="py-1.5 text-right font-heading font-bold text-slate-900">{t.aantal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-400 mt-3">Functienamen uit de Functiecheck die de spreektaal-laag nog niet herkent. Geanonimiseerd: alleen de term wordt bewaard, geen gebruiker.</p>
      </Card>

      {/* Functiecheck-feedback (pilot, beheerder + superbeheerder) */}
      {isAdmin && <AkkoordenCard />}

      {isAdmin && <FunctiecheckFeedback />}

      {/* Taallaag-inzicht (superbeheerder) — beslisondersteuning Fase 2 */}
      {isSuper && data.taallaag && (
        <Card title="Taallaag — meertaligheid & effectiviteit (superbeheerder)" icon={Sparkles} accent="text-cyan-600" testid="card-taallaag">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="taal-kpi-gebruikt">
              <p className="text-[11px] text-slate-500">Taallaag gebruikt</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.taallaag.gebruikt}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-slate-50">
              <p className="text-[11px] text-slate-500">Overgeslagen</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.taallaag.overgeslagen}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-cyan-50">
              <p className="text-[11px] text-cyan-700">% gebruikt</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.taallaag.percentage_gebruikt}%</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-emerald-50">
              <p className="text-[11px] text-emerald-700">ROOD → GROEN/GEEL</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.taallaag.effectiviteit.rood_naar_groen + data.taallaag.effectiviteit.rood_naar_geel}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Vragen per taal</p>
              <BarList items={data.taallaag.per_taal} testid="taal-per-taal" />
              <p className="text-xs font-semibold text-slate-500 mt-4 mb-2">Vertalingen naar Nederlands</p>
              <BarList items={data.taallaag.top_vertalingen} empty="Nog geen vertalingen." testid="taal-vertalingen" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Meest voorkomende taalcorrecties</p>
              <ul className="space-y-1.5" data-testid="taal-correcties">
                {data.taallaag.top_correcties.length ? data.taallaag.top_correcties.map((c, i) => (
                  <li key={i} className="flex justify-between gap-2 text-xs bg-slate-50 rounded px-2 py-1 min-w-0">
                    <span className="text-slate-700 truncate min-w-0">{c.label}</span>
                    <span className="font-bold text-slate-900 shrink-0">{c.aantal}×</span>
                  </li>
                )) : <li className="text-xs text-slate-400 italic">Nog geen correcties.</li>}
              </ul>
              <p className="text-xs font-semibold text-slate-500 mt-4 mb-2">Effectiviteit (na normalisatie)</p>
              <div className="space-y-1 text-xs" data-testid="taal-effectiviteit">
                <div className="flex justify-between"><span className="text-emerald-700">ROOD → GROEN</span><span className="font-bold">{data.taallaag.effectiviteit.rood_naar_groen}</span></div>
                <div className="flex justify-between"><span className="text-amber-700">ROOD → GEEL</span><span className="font-bold">{data.taallaag.effectiviteit.rood_naar_geel}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">GEEL → GROEN</span><span className="font-bold">{data.taallaag.effectiviteit.geel_naar_groen}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Geen verbetering</span><span className="font-bold">{data.taallaag.effectiviteit.geen_verbetering}</span></div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Pilot-overzicht (superbeheerder) */}
      {isSuper && data.pilot && (
        <Card title="Pilot — status & feedback (superbeheerder)" icon={UserCheck} accent="text-[#B9772B]" testid="card-pilot">
          <p className="text-xs text-slate-500 mb-4">Overzicht van de pilotfase: actieve deelnemers, antwoordfeedback en doorverwijzingen naar de Salarischeck.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            <div className="rounded-lg px-3 py-2 bg-[#FBF3E3] border border-[#E4D3B8]" data-testid="pilot-kpi-actief">
              <p className="text-[11px] text-[#8A6B47]">Actieve pilotgebruikers</p>
              <p className="text-lg font-heading font-bold text-[#3A281B]">{data.pilot.actieve_gebruikers}/{data.pilot.max_gebruikers}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-200" data-testid="pilot-kpi-beschikbaar">
              <p className="text-[11px] text-emerald-700">Beschikbaar</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.pilot.beschikbaar}/{data.pilot.max_gebruikers}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-green-50 border border-green-200" data-testid="pilot-kpi-nuttig">
              <p className="text-[11px] text-green-700 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Nuttig</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.pilot.feedback_nuttig}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-red-50 border border-red-200" data-testid="pilot-kpi-nietnuttig">
              <p className="text-[11px] text-red-700 flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Niet nuttig</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.pilot.feedback_niet_nuttig}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-slate-50 border border-slate-200" data-testid="pilot-kpi-salarischeck">
              <p className="text-[11px] text-slate-500">Naar Salarischeck</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.pilot.salarischeck_doorverwezen}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">Meest gestelde onderwerpen</p>
            <QuestionList items={data.gebruik.meest_gestelde} empty="Nog geen vragen." testid="pilot-meest-gestelde" />
          </div>
        </Card>
      )}

      {/* Aanmeldingen (superbeheerder) — pilotmonitoring, beveiliging & adoptie Google Login */}
      {isSuper && data.aanmeldingen && (
        <Card title="Aanmeldingen — monitoring & beveiliging (superbeheerder)" icon={LogIn} accent="text-blue-600" testid="card-aanmeldingen">
          <p className="text-xs text-slate-500 mb-4">Geanonimiseerd (e-mail gemaskeerd). Inzicht in adoptie van Google Login en signalering van mislukte aanmeldingen.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
            <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="aanm-kpi-totaal">
              <p className="text-[11px] text-slate-500">Totaal</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.totaal}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-emerald-50" data-testid="aanm-kpi-succes">
              <p className="text-[11px] text-emerald-700">Succesvol</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.succesvol}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-rose-50" data-testid="aanm-kpi-mislukt">
              <p className="text-[11px] text-rose-700">Mislukt</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.mislukt}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="aanm-kpi-wachtwoord">
              <p className="text-[11px] text-slate-500 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Wachtwoord</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.wachtwoord}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-blue-50" data-testid="aanm-kpi-google">
              <p className="text-[11px] text-blue-700">Google</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.google}</p>
            </div>
            <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="aanm-kpi-uniek">
              <p className="text-[11px] text-slate-500">Unieke gebruikers</p>
              <p className="text-lg font-heading font-bold text-slate-900">{data.aanmeldingen.unieke_gebruikers}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Trend</p>
              <div className="space-y-2 text-xs" data-testid="aanm-trend">
                {[["Afgelopen 7 dagen", data.aanmeldingen.trend_7d], ["Afgelopen 30 dagen", data.aanmeldingen.trend_30d]].map(([lbl, t]) => (
                  <div key={lbl} className="bg-slate-50 rounded-lg px-3 py-2">
                    <div className="flex justify-between font-medium text-slate-700 mb-1"><span>{lbl}</span><span>{t.totaal} totaal</span></div>
                    <div className="flex gap-3 text-[11px] text-slate-500">
                      <span><KeyRound className="w-3 h-3 inline mr-0.5" />{t.wachtwoord} wachtwoord</span>
                      <span className="text-blue-600">{t.google} Google</span>
                      <span className="text-rose-600">{t.mislukt} mislukt</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Laatste aanmeldingen</p>
              <ul className="space-y-1.5" data-testid="aanm-laatste">
                {data.aanmeldingen.laatste.length ? data.aanmeldingen.laatste.map((a, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.succes ? "bg-emerald-500" : "bg-rose-500"}`} />
                      <span className="text-slate-700 truncate min-w-0">{a.gebruiker}</span>
                      <span className="text-slate-400 shrink-0">· {a.rol || "—"}</span>
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${a.methode === "google" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>{a.methode}</span>
                      <span className="text-slate-400">{fmtTijd(a.datumtijd)}</span>
                    </span>
                  </li>
                )) : <li className="text-xs text-slate-400 italic">Nog geen aanmeldingen.</li>}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* P2A — Superbeheerder: actief begrippenbeheer */}
      {isSuper && (
        <Card title="Ontbrekende begrippen beheren (superbeheerder)" icon={Sparkles} accent="text-violet-600" testid="card-begrippenbeheer">
          <p className="text-xs text-slate-500 mb-3">Klik op een term om deze toe te voegen aan het begrippenregister, als synoniem te koppelen, te negeren of later te beoordelen.</p>
          {missing.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Geen ontbrekende begrippen om te beheren.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="beheer-lijst">
              {missing.map((t) => (
                <li key={t.term}>
                  <button
                    data-testid={`beheer-term-${t.term}`}
                    onClick={() => openTerm(t)}
                    className="w-full py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-slate-800 truncate min-w-0">{t.term}</span>
                      <Badge variant="secondary" className="text-[10px] shrink-0">{t.aantal}×</Badge>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* P2A — Superbeheerder: pilot-reset & export */}
      {isSuper && (
        <Card title="Pilot-beheer (superbeheerder)" icon={ShieldCheck} accent="text-rose-600" testid="card-pilotbeheer">
          <p className="text-xs text-slate-500 mb-3">Start een schone nulmeting vóór de pilot. Exporteer eerst de bestaande logs als backup.</p>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={exporteer} data-testid="btn-export" className="gap-2">
              <Download className="w-4 h-4" /> Auditlogs exporteren
            </Button>
            <Button variant="outline" onClick={downloadPilotrapport} data-testid="btn-pilotrapport" className="gap-2">
              <Download className="w-4 h-4" /> Pilotrapport (evaluatie)
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="btn-reset" className="gap-2">
                  <Trash2 className="w-4 h-4" /> Auditlogs resetten
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="reset-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Weet je zeker dat je alle auditlogs wilt resetten?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Alle auditlogs en statistieken worden definitief verwijderd voor een schone
                    pilot-nulmeting. Exporteer eerst een backup als je de data wilt bewaren. Deze
                    actie kan niet ongedaan worden gemaakt.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="reset-cancel">Annuleren</AlertDialogCancel>
                  <AlertDialogAction onClick={resetLogs} disabled={resetting} data-testid="reset-confirm" className="bg-rose-600 hover:bg-rose-700">
                    {resetting ? "Bezig…" : "Ja, alles resetten"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>
      )}

      {/* Begrip-actie dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="term-dialog" className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" /> Begrip beheren: “{selected.term}”
                </DialogTitle>
                <DialogDescription>{selected.aantal}× gezien in ROOD-vragen.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Voorbeeldvragen</p>
                  <ul className="space-y-1">
                    {selected.voorbeelden.map((v, i) => (
                      <li key={i} className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">{v}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Eerst: {(selected.eerste_gezien || "").slice(0, 10) || "—"} · Laatst: {(selected.laatste_gezien || "").slice(0, 10) || "—"}</span>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Voorgestelde normalisatie / officiële term</label>
                  <Input value={mapsTo} onChange={(e) => setMapsTo(e.target.value)} data-testid="term-normalisatie" placeholder="bijv. arbeidsduurverkorting" />
                </div>

                {selected.vergelijkbare_begrippen.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">Vergelijkbare bestaande begrippen (kies voor synoniem)</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.vergelijkbare_begrippen.map((b) => (
                        <button
                          key={b.id}
                          data-testid={`sim-${b.id}`}
                          onClick={() => { setTargetId(b.id); setMapsTo(b.term); }}
                          className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${targetId === b.id ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:border-violet-400"}`}
                        >
                          {b.term}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button onClick={() => voerActieUit("add_term")} disabled={saving || !mapsTo.trim()} data-testid="actie-add-term" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4" /> Toevoegen aan register
                </Button>
                <Button onClick={() => voerActieUit("add_synonym")} disabled={saving || !targetId} variant="outline" data-testid="actie-add-synonym" className="gap-1.5">
                  <Link2 className="w-4 h-4" /> Als synoniem
                </Button>
                <Button onClick={() => voerActieUit("ignore")} disabled={saving} variant="ghost" data-testid="actie-ignore" className="gap-1.5 text-slate-500">
                  <EyeOff className="w-4 h-4" /> Negeren
                </Button>
                <Button onClick={() => voerActieUit("review")} disabled={saving} variant="ghost" data-testid="actie-review" className="gap-1.5 text-slate-500">
                  <Clock className="w-4 h-4" /> Later
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
